import { motion } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";

interface Props {
    id: string;
    name: string;
    description: string;
    logoUrl: string;
    badgeText: string;
    badgeColor?: "orange" | "green" | "blue";
    selected: boolean;
    onSelect: (id: string) => void;
    disabled?: boolean;
}

export function PaymentMethodCard({
    id, name, description, logoUrl, badgeText, badgeColor = "blue",
    selected, onSelect, disabled = false
}: Props) {

    // Warna dinamis untuk badge IDR/USD
    const badgeStyles = {
        orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
        green: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
        blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
    };

    return (
        <motion.div
            whileHover={!disabled ? { y: -4 } : {}}
            whileTap={!disabled ? { scale: 0.98 } : {}}
            className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ease-in-out
                ${disabled ? "opacity-50 grayscale cursor-not-allowed bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800" : ""}
                ${selected && !disabled
                    ? "border-blue-600 bg-white dark:bg-blue-900/20 shadow-md ring-2 ring-blue-600 dark:ring-blue-500" // <-- Penambahan ring-2 di sini
                    : !disabled ? "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/80" : ""
                }`}
            onClick={() => !disabled && onSelect(id)}
        >
            {/* Logo Kiri */}
            <div className="shrink-0 w-14 flex items-center justify-center bg-white p-1 rounded-md border border-slate-100 dark:border-slate-200/20">
                <img src={logoUrl} alt="Logo Pembayaran" className="w-full h-auto object-contain" />
            </div>

            {/* Garis Pembatas Vertikal */}
            <div className="w-[1.5px] h-10 bg-slate-200 dark:bg-slate-700 mx-4 shrink-0 rounded-full"></div>

            {/* Konten Teks */}
            <div className="flex flex-col flex-grow pr-8">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className={`font-bold text-sm transition-colors ${selected ? "text-blue-900 dark:text-blue-100" : "text-slate-900 dark:text-slate-100"}`}>
                        {name}
                    </span>
                    {/* Badge Dinamis (IDR/USD) */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${badgeStyles[badgeColor]}`}>
                        {badgeText}
                    </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                    {description}
                </p>
            </div>

            {/* Checkmark / Lock Indikator Kanan */}
            <div className="absolute right-4 flex items-center justify-center">
                {selected ? (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                        <CheckCircle2 className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" stroke="white" />
                    </motion.div>
                ) : disabled ? (
                    <Lock className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                ) : null}
            </div>
        </motion.div>
    );
}